// Field suggestions — the deterministic rungs of the ladder.
//
// CONSUMER: shared (web prep screen + node-pool tests).
//
// Grant, 2026-08-22, on why this exists: he does not want his team
// drawing boxes on every document. The engine runs top-down, cheapest
// and most certain first, and each rung only fills gaps the one above
// left:
//
//   1. saved layout match      (fingerprint the document)
//   2. AcroForm widgets        HERE — exact geometry, zero guessing
//   3. drawn ruled blanks      shared/esign/vector-blanks.ts — the lines
//                              a form PAINTS rather than types, which is
//                              how every AAR form marks a blank
//   4. text-layer heuristics   HERE — underscore runs and labels
//   5. Stew's semantic pass    (server; anchors to spans this produces)
//
// Pure and dependency-free, same rule as field-types.ts: the web bundle
// and the node-pool tests import it without dragging in DB or fetch
// code, which is also what makes every rule here cheap to test against
// real-looking input.
//
// EVERY output is a PROPOSAL. Nothing here places a field — the prep
// screen renders these as dashed, provenance-labelled fields the advisor
// accepts, adjusts, or deletes (the plan's "recommendation-first,
// override-honest" rule). A suggestion that is wrong must be obviously
// wrong on screen, never silently authoritative.

import type { EsignTextStyle } from "./text-style";
import { bindingForLabel, type BindingLookup } from "./label-bindings";
import {
  LINE_BOX_RATIO,
  MAX_FIELD_FONT_PT,
  MIN_FIELD_FONT_PT,
  type EsignFieldType,
} from "./field-types";

/** Where a suggestion came from. Shown to the advisor verbatim, because
 *  "the document told us" and "Stew guessed" deserve different trust. */
export type SuggestionSource =
  | "form_field"
  | "ruled_line"
  | "text_layer"
  | "stew"
  | "saved_layout";

export interface SuggestedField {
  fieldType: EsignFieldType;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  source: SuggestionSource;
  /** 0–1. Drives ordering and the "check this one" flag, never a silent
   *  drop: a low-confidence suggestion is still shown, labelled. */
  confidence: number;
  /** Plain English, shown on the field chip: "Signature line on page 2".
   *  Not a debug string — an advisor reads this to decide. */
  reason: string;
  /** Which signer this looks like it belongs to, when the document says
   *  so ("Buyer", "Seller"). Null means "you pick" — never a guess
   *  dressed as an answer. */
  signerHint: string | null;
  /** The record field this blank is ASKING FOR, when its label says so
   *  (see label-bindings.ts). A bound proposal arrives filled from the
   *  record instead of empty — which is the difference between a
   *  contract an advisor reviews and one they retype. Absent on every
   *  proposal that is a place to SIGN rather than a place data goes. */
  mergeToken?: string | null;
  /** Points — the size of the type this blank is surrounded by, read
   *  off the caption naming it (or off the underscore run drawing it).
   *
   *  Carried so a value prints at the document's own size rather than
   *  the signing service's web-form default, which is what made Baker's
   *  disclosures read as "off" on 2026-09-18. Absent when the rung that
   *  proposed the field had no type to measure. */
  fontSize?: number;
  /** A text box spanning several printed lines (Grant, 2026-09-29): the
   *  points between the rules it sits on, and how far in its first line
   *  starts. Set by the ruled-line rung when it finds a stacked run of
   *  rules, and by a saved map that was taught one. Absent everywhere
   *  else. See shared/esign/multiline-text.ts. */
  multiline?: boolean;
  linePitch?: number;
  firstLineIndent?: number;
  /** How a locked text box's type looks, from a saved map (2026-10-26). */
  textStyle?: EsignTextStyle;
  /** A term from the offer this send is drafted from (Nate, 2026-11-03):
   *  see OfferTermOffer in layout-apply.ts. */
  offerTerm?: { value: string; label: string };
}

/** Geometry we invent when a rung knows WHERE but not HOW BIG — a text
 *  label tells us a signature goes on this line, not that the box is
 *  180×28. These are the prep screen's own defaults so a suggested
 *  field and a hand-placed one look identical once accepted. */
/** Below this a box is untappable, so a freak 2pt underscore run gets
 *  widened to something a person can actually hit. Matches the prep
 *  screen's own MIN_FIELD_SIZE_PT floor. */
const MIN_SUGGESTED_WIDTH = 8;
const MIN_SUGGESTED_HEIGHT = 8;

/** However small a form's print gets, a text box below this is neither
 *  readable nor tappable. Mirrors vector-blanks.ts's own floor. */
const MIN_SUGGESTED_TEXT_HEIGHT = 12;

const DEFAULT_SIZE: Record<EsignFieldType, { width: number; height: number }> = {
  signature: { width: 180, height: 28 },
  initials: { width: 56, height: 28 },
  date_signed: { width: 110, height: 24 },
  text: { width: 160, height: 24 },
  checkbox: { width: 16, height: 16 },
  dropdown: { width: 160, height: 24 },
  radio: { width: 16, height: 16 },
  editable_date: { width: 110, height: 24 },
  attachment: { width: 160, height: 28 },
};

// ─── Rung 2: the PDF's own form fields ──────────────────────────────

export interface WidgetInput {
  pageNumber: number;
  /** pdf.js field type: Sig / Tx / Btn / Ch. */
  fieldType: string;
  fieldName: string | null;
  readOnly: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Names that tell us what a text widget is really for. Ordered: the
 *  first match wins, so "date signed" resolves as a date rather than
 *  matching a later, looser rule. */
const NAME_RULES: { pattern: RegExp; type: EsignFieldType }[] = [
  { pattern: /\b(sign(ature)?|esign)\b/i, type: "signature" },
  { pattern: /\binitial/i, type: "initials" },
  { pattern: /\bdate\b/i, type: "date_signed" },
];

/** Signer roles a document names out loud. Deliberately short: these
 *  are the words Stewardship's own documents use, and a hint we can't
 *  make confidently is better left null (the advisor assigns it in one
 *  click) than guessed.
 *
 *  ONE list, shared with the ruled-line rung (vector-blanks.ts), which
 *  kept its own copy in a different order until 2026-10-13 — so the
 *  same caption meant "Buyer" to one rung and "Seller" to the other.
 *  The co-borrower spellings come before "borrower", which they
 *  contain. */
export const ROLE_WORDS = [
  "co-borrower",
  "coborrower",
  "buyer",
  "seller",
  "borrower",
  "lender",
  "landlord",
  "tenant",
  "insured",
  "owner",
  "applicant",
  "agent",
  "broker",
  "advisor",
  "client",
  "witness",
  "trustee",
  "spouse",
];

/**
 * Field names in real PDFs are machine names, not prose: "DateSigned",
 * "sign_here_2", "Buyer.Signature". Word-boundary patterns match none
 * of those — `\bdate\b` fails on "DateSigned" because there is no
 * boundary between "e" and "S" — so every name is flattened to spaced
 * lowercase words BEFORE any rule sees it.
 *
 * Found by the test for exactly this: "DateSigned" resolved to a plain
 * text box, which on a real contract means an advisor hand-fixing the
 * date field on every send.
 */
function normalizeName(raw: string | null): string {
  if (!raw) return "";
  return raw
    // camelCase / PascalCase → separate words
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    // separators people actually use in field names
    .replace(/[._\-/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Which occurrence of each role word a rung has already seen, in
 * document order — the counter that keeps "BUYER SIGNATURE" from
 * meaning the same buyer twice.
 *
 * A printed form often repeats the exact same words for its first and
 * second signer with no number on the page at all (unlike the reviewed
 * field atlas's own captions, which spell out "(1)"/"(2)"). Without
 * this, every `roleFrom` call for the same role returns the identical
 * bare word, and `matchCaptionToRole` (signing-roles.ts) — whose
 * catalog gives the bare word "buyer" ONLY to the first buyer's seat —
 * routes every one of them to the same person: Nate, 2026-09-23, both
 * signature lines on the Buyer Advisory landed on Emilie, none on Eric.
 *
 * One counter per call to a `suggestFrom*` function, so ordinals reset
 * per rung's own read of the document rather than accumulating oddly
 * across unrelated calls.
 */
export type RoleOccurrenceCounts = Map<string, number>;

/** The parties a contract signature block can belong to. A caption
 *  naming two or more of them names none of them. */
const PRINCIPAL_PARTIES: readonly { word: RegExp; party: string }[] = [
  { word: /\bbuyers?\b/, party: "buyer" },
  { word: /\bsellers?\b/, party: "seller" },
  // AAR pairs them as one party: "Seller/Landlord", "Buyer/Tenant".
  { word: /\blandlords?\b/, party: "seller" },
  { word: /\btenants?\b/, party: "buyer" },
  { word: /\b(co-?)?borrowers?\b/, party: "borrower" },
  // "Owner" on a listing is the seller or the landlord, so it is the
  // same party as either, never a second one beside them.
  { word: /\bowners?\b/, party: "seller" },
];

/**
 * True when a caption names several parties at once — every signature
 * block on an AAR addendum or counter offer prints "Seller Buyer
 * Landlord Tenant" under the line for the signer to tick.
 *
 * Until 2026-10-13 both rungs took the FIRST role word in their own
 * list, so all four blocks on Nate's Addendum came back "Buyer",
 * "Buyer (2)", "Buyer (3)"… and every one of them fell to the first
 * signer.
 */
export function namesSeveralParties(text: string): boolean {
  const lower = text.toLowerCase();
  const found = new Set<string>();
  for (const { word, party } of PRINCIPAL_PARTIES) if (word.test(lower)) found.add(party);
  return found.size >= 2;
}

/**
 * The role a caption names, numbered by occurrence when a counter is
 * passed ("Buyer", then "Buyer (2)"). Null when the caption names no
 * role, or names several parties — and an ambiguous caption does not
 * advance the counter, so the next "BUYER SIGNATURE" is still Buyer 1's
 * or Buyer 2's as printed.
 */
export function roleWordFromCaption(
  text: string | null,
  occurrences?: RoleOccurrenceCounts,
): string | null {
  if (!text) return null;
  const lower = normalizeName(text) || text.toLowerCase();
  if (namesSeveralParties(lower)) return null;
  for (const role of ROLE_WORDS) {
    if (lower.includes(role)) {
      // Title-case for display: the chip says "Buyer", not "buyer".
      const label = role.charAt(0).toUpperCase() + role.slice(1);
      if (!occurrences) return label;
      // "(2)", "(3)", … — the same notation matchCaptionToRole already
      // parses to redirect a hint to a seat's second (or later) occupant.
      const seen = (occurrences.get(role) ?? 0) + 1;
      occurrences.set(role, seen);
      return seen > 1 ? `${label} (${seen})` : label;
    }
  }
  return null;
}

function roleFrom(text: string | null, occurrences?: RoleOccurrenceCounts): string | null {
  return roleWordFromCaption(text, occurrences);
}

/**
 * Rung 2. A PDF that carries real form fields has already told us where
 * every field is, to the point — no heuristics, no model, no guessing.
 * This is both the cheapest rung and the most accurate, which is why it
 * runs before anything clever.
 *
 * Read-only widgets are skipped: they are printed data, not somewhere a
 * person acts.
 */
export function suggestFromWidgets(widgets: WidgetInput[]): SuggestedField[] {
  const out: SuggestedField[] = [];
  const roleOccurrences: RoleOccurrenceCounts = new Map();
  for (const w of widgets) {
    if (w.readOnly) continue;
    if (w.width <= 0 || w.height <= 0) continue;

    let type: EsignFieldType | null = null;
    if (w.fieldType === "Sig") {
      type = "signature";
    } else if (w.fieldType === "Btn") {
      type = "checkbox";
    } else if (w.fieldType === "Tx") {
      const name = normalizeName(w.fieldName);
      type = NAME_RULES.find((r) => r.pattern.test(name))?.type ?? "text";
    }
    // "Ch" (dropdown/list) has no equivalent in our vocabulary — a
    // signer can't pick from a list on a signed PDF — so it is skipped
    // rather than forced into a text box that would lose the choices.
    if (!type) continue;

    out.push({
      fieldType: type,
      pageNumber: w.pageNumber,
      x: w.x,
      y: w.y,
      width: w.width,
      height: w.height,
      source: "form_field",
      // The document declared this. Nothing here is inferred except
      // which of OUR types it maps to.
      confidence: 0.95,
      reason: w.fieldName
        ? `Form field "${w.fieldName}" on page ${w.pageNumber}`
        : `Form field on page ${w.pageNumber}`,
      signerHint: roleFrom(w.fieldName, roleOccurrences),
    });
  }
  return out;
}

// ─── Rung 3: the text layer ─────────────────────────────────────────

export interface SpanInput {
  id: string;
  pageNumber: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * TEXT A SIGNING SERVICE STAMPED ON THE PAGE, not text the form printed:
 * "dotloop signature verification: dtlp.us/…", "DocuSign Envelope ID:
 * …", "dotloop verified 10/12/26 11:35 AM MST …". It names nothing a
 * person fills in — "signature verification" was read as a signature
 * caption, and Nate's Addendum opened with a signature box for the
 * seller beside dotloop's link at the top of the page (2026-10-13).
 *
 * Exported so the ruled-line rung and the prep screen skip the same
 * spans this one does.
 */
const PROVIDER_STAMP =
  /\b(dotloop|dtlp\.us|docu\s?sign(ed)?|envelope\s+id|authentisign|skyslope|digitally\s+signed\s+by|electronically\s+signed)\b/i;

export function isProviderStamp(text: string): boolean {
  return PROVIDER_STAMP.test(text);
}

/** A run of underscores (or a long low line of them) is the oldest
 *  signature affordance in paper forms and still the most common. Four
 *  is the floor — shorter runs appear inside ordinary text. */
const UNDERSCORE_RUN = /_{4,}/;

/** Labels that name what goes on the line beside or below them. Each
 *  maps to the type it implies; order matters for the same reason as
 *  NAME_RULES. */
export const LABEL_RULES: {
  pattern: RegExp;
  type: EsignFieldType;
  what: string;
  /** The caption is printed UNDER its line, not beside it. The text
   *  rung, which places a box to the RIGHT of a caption, skips these:
   *  a box beside "MO/DA/YR" lands on whatever the line above says next
   *  (Nate's Addendum: a date box over "between the following
   *  Parties"). The ruled-line rung reads below-line captions with the
   *  line's real geometry and owns them. */
  below?: boolean;
}[] = [
  { pattern: /\bdate\s*(signed)?\s*[:_]?\s*$/i, type: "date_signed", what: "Date line" },
  { pattern: /\binitials?\s*[:_]?\s*$/i, type: "initials", what: "Initials line" },
  {
    pattern: /\b(signature|sign\s+here|signed\s+by)\b/i,
    type: "signature",
    what: "Signature line",
  },
  // "By:" and a bare "X" are the two shorthand signature marks that
  // appear without the word "signature" anywhere near them.
  { pattern: /^\s*by\s*[:_]\s*$/i, type: "signature", what: "Signature line" },
  { pattern: /^\s*x\s*_{2,}\s*$/i, type: "signature", what: "Signature line" },
  // AAR prints its date captions as a format string rather than the
  // word "date" — "MO/DA/YR" under the line in the signature block of
  // every form they publish. Nate's listing contract has six of them.
  { pattern: /\bmo\s*\/\s*da\s*\/\s*yr\b/i, type: "date_signed", what: "Date line", below: true },
  {
    pattern: /^\s*(mm|mo)\s*[/-]\s*(dd|da)\s*[/-]\s*(yy|yyyy|yr)\s*$/i,
    type: "date_signed",
    what: "Date line",
    below: true,
  },
];

/**
 * What a caption says a blank is CEREMONIALLY for — somewhere a person
 * signs, initials or dates — or null when it names something else.
 *
 * Exported so the ruled-line rung (shared/esign/vector-blanks.ts) types
 * a caption exactly as this rung does. Two copies of this judgement
 * would drift, and the drift would show up as one rung calling a line
 * a signature while the other calls it a date.
 */
export function ceremonyTypeForLabel(
  text: string,
): { type: EsignFieldType; what: string } | null {
  const label = text.replace(/[\s:_.·—–-]+$/g, "").trim();
  if (!label || isProviderStamp(label)) return null;
  const hit = LABEL_RULES.find((r) => r.pattern.test(label));
  return hit ? { type: hit.type, what: hit.what } : null;
}

/**
 * The narrowest unlabelled blank we will still call a signature line.
 *
 * DECISION (2026-09-01, Nate's listing contract): an unlabelled
 * underscore run used to become a SIGNATURE unconditionally. On a
 * signature page that is right; across the 27-form AAR catalog it is
 * wrong far more often than it is right, because those forms are mostly
 * inline data blanks — a county, a date, a dollar amount — and every one
 * of them was being proposed as somewhere to sign.
 *
 * Width is the honest discriminator available to a single span: nobody
 * signs their name in a forty-point gap, and AAR's own signature rules
 * are the ~120pt side-by-side columns this file already measures (see
 * suggestFromText's width note). Below the floor a blank is data.
 *
 * The cost asymmetry backs the change: a data blank mistyped as text is
 * an empty box someone types into, while a signature field in the wrong
 * place is a ceremony element on a legal document that looks deliberate
 * — which is exactly what Nate reported ("two spots for initials but
 * they are incorrectly placed").
 */
const SIGNATURE_RUN_MIN_WIDTH = 110;

export interface TextSuggestOptions {
  /** Enables the label dictionary — what each blank is ASKING FOR. Left
   *  out (a caller with no record in hand), every proposal comes back
   *  unbound and this rung behaves exactly as it did before. */
  binding?: BindingLookup;
}

/**
 * Rung 3. Deterministic candidates from the text layer — no model, no
 * cost, and it works on the majority of the forms Stewardship actually
 * sends, which are typed documents with ruled lines.
 *
 * Three shapes are recognized:
 *   * an underscore run — the field goes ON the run, sized to it;
 *   * a ceremony label ("Signature:", "Date:") — the field goes to the
 *     RIGHT of the label on the same line, at the default size;
 *   * a DATA label the dictionary recognizes ("Assessor's #", "County")
 *     — same placement, typed as text and bound to the record field the
 *     label is asking for.
 *
 * The third shape is what makes this rung work on AAR's forms at all.
 * They rule their blanks as drawn lines rather than typing underscores,
 * so the blank itself is invisible to a text layer — but the CAPTION
 * beside it is not. Reading the caption finds the field the line would
 * have; it is also why the listing contract came back with almost
 * nothing before this existed.
 *
 * A page with no text layer yields nothing, which is the honest answer:
 * a scan is a job for manual placement (or the AI pass on its image),
 * not for a heuristic pretending to read it.
 */
export function suggestFromText(
  spans: SpanInput[],
  options: TextSuggestOptions = {},
): SuggestedField[] {
  const out: SuggestedField[] = [];
  const binding = options.binding;
  const roleOccurrences: RoleOccurrenceCounts = new Map();

  for (const span of spans) {
    const text = span.text;
    if (isProviderStamp(text)) continue;

    // An underscore run tells us the exact extent of the line, so it
    // beats a label: use the run's own geometry.
    if (UNDERSCORE_RUN.test(text)) {
      // A label sitting on the SAME span before the underscores names
      // the run ("Signature: _______"). The label rules are end-anchored
      // (a bare "Signature:" ends its span), so the run has to come off
      // before they are tested — otherwise a labelled line is typed as a
      // generic signature and every date line on the document is wrong.
      const withoutRun = text.replace(/_{4,}.*$/, "").trim();
      const labelled = LABEL_RULES.find((r) => r.pattern.test(withoutRun));
      // A ceremony label wins outright; failing that, ask the dictionary
      // what this blank is for; failing THAT, fall back on width.
      const bound = labelled ? null : binding ? bindingForLabel(withoutRun, binding) : null;
      const type: EsignFieldType = labelled
        ? labelled.type
        : bound
          ? "text"
          : span.width >= SIGNATURE_RUN_MIN_WIDTH
            ? "signature"
            : "text";
      const size = DEFAULT_SIZE[type];
      // THE RUN'S OWN TYPE SIZE (Baker, 2026-09-18). A row of
      // underscores is set in the form's body type, so it measures the
      // value that goes on it exactly as a caption does for a drawn
      // line — see the same reasoning, and the same signature/date
      // exclusion, in vector-blanks.ts.
      const runPt =
        Number.isFinite(span.height) &&
        span.height >= MIN_FIELD_FONT_PT &&
        span.height <= MAX_FIELD_FONT_PT
          ? span.height
          : null;
      const boxHeight =
        type === "text" && runPt
          ? Math.max(MIN_SUGGESTED_TEXT_HEIGHT, runPt * LINE_BOX_RATIO)
          : size.height;
      const what = labelled?.what ?? bound?.label ?? (type === "signature" ? "Signature line" : "Blank");
      out.push({
        fieldType: type,
        ...(bound ? { mergeToken: bound.token } : {}),
        ...(type === "text" && runPt ? { fontSize: runPt } : {}),
        pageNumber: span.pageNumber,
        x: span.x,
        // The field sits ON the line, so its box rises above the run's
        // baseline rather than starting at it. Stated as "the box's
        // bottom edge IS the run's bottom edge", which is the invariant
        // the height above must not break.
        y: Math.max(0, span.y + span.height - boxHeight),
        // The RUN'S OWN WIDTH, not a minimum against the default. An
        // underscore run measures the line exactly, and widening it to
        // the 180pt default is what pushed fields off the right edge of
        // AAR contracts (staging round 3, 2026-08-23) and made
        // side-by-side BUYER/SELLER columns overlap: those lines are
        // ~120pt, and no amount of clamping makes a 180pt box correct
        // there. A floor only guards against a stray 2pt run.
        width: Math.max(span.width, MIN_SUGGESTED_WIDTH),
        height: boxHeight,
        source: "text_layer",
        // A recognized label — ceremony or data — is real evidence about
        // what this blank is. A width guess is not, and is scored below
        // both so the merge prefers anything better.
        confidence: labelled ? 0.75 : bound ? 0.7 : 0.6,
        reason: `${what} on page ${span.pageNumber}`,
        signerHint: roleFrom(text, roleOccurrences),
      });
      continue;
    }

    // No underscore run — so this is a caption whose blank is a DRAWN
    // line the text layer cannot see. The label still names the field.
    const labelled = LABEL_RULES.find((r) => r.pattern.test(text));
    if (labelled?.below) continue;
    const boundLabel = labelled ? null : binding ? bindingForLabel(text, binding) : null;
    if (!labelled && !boundLabel) continue;
    const labelType: EsignFieldType = labelled ? labelled.type : "text";
    const size = DEFAULT_SIZE[labelType];
    out.push({
      fieldType: labelType,
      ...(boundLabel ? { mergeToken: boundLabel.token } : {}),
      pageNumber: span.pageNumber,
      // Beside the label, with a small gap — where a person would write.
      x: span.x + span.width + 6,
      y: Math.max(0, span.y - Math.max(0, size.height - span.height)),
      width: size.width,
      height: size.height,
      source: "text_layer",
      confidence: 0.55,
      reason: `${labelled?.what ?? boundLabel?.label ?? "Blank"} on page ${span.pageNumber}`,
      signerHint: roleFrom(text, roleOccurrences),
    });
  }

  return out;
}

// ─── Merging the rungs ──────────────────────────────────────────────

/** How much two boxes must overlap to be "the same field". Generous:
 *  two rungs describing one signature line rarely agree to the point,
 *  and showing the advisor the same line twice is worse than picking
 *  the more certain of the two. */
const SAME_FIELD_OVERLAP = 0.35;

/** Two SIGNING marks that overlap at all are one too many. A date box
 *  drawn a third of the way into a signature box was "a different
 *  field" at 0.35, and Nate's Addendum showed a date sitting on top of
 *  the seller's signature (2026-10-13). Ten percent leaves room for two
 *  boxes that merely touch at the edge. */
const SAME_SIGNING_MARK_OVERLAP = 0.1;
const SIGNING_MARK: ReadonlySet<EsignFieldType> = new Set(["signature", "initials", "date_signed"]);

function overlapRatio(a: SuggestedField, b: SuggestedField): number {
  if (a.pageNumber !== b.pageNumber) return 0;
  const x = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const y = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  const inter = x * y;
  if (inter <= 0) return 0;
  const smaller = Math.min(a.width * a.height, b.width * b.height);
  return smaller > 0 ? inter / smaller : 0;
}

/** Rung order — earlier is more trusted, and ties never go to a guess. */
const SOURCE_RANK: Record<SuggestionSource, number> = {
  saved_layout: 0,
  form_field: 1,
  // A drawn rule is the document stating where it left room — evidence
  // of the same kind an AcroForm widget is, just without a declared
  // type. It outranks the text heuristics, which infer a blank's
  // existence from the words near it. See shared/esign/vector-blanks.ts.
  ruled_line: 2,
  text_layer: 3,
  stew: 4,
};

/**
 * Fold the rungs into one list, keeping the most trustworthy suggestion
 * wherever two describe the same spot.
 *
 * "Each rung only fills gaps the one above left" is implemented HERE
 * rather than by short-circuiting the rungs themselves — every rung
 * runs (they are all cheap except Stew's, which the caller gates), and
 * this decides. That way a form field and a text line that genuinely
 * describe different spots both survive, which short-circuiting on
 * "did rung 2 find anything?" would lose.
 */
export function mergeSuggestions(...groups: SuggestedField[][]): SuggestedField[] {
  const all = groups.flat().sort((a, b) => {
    const rank = SOURCE_RANK[a.source] - SOURCE_RANK[b.source];
    if (rank !== 0) return rank;
    return b.confidence - a.confidence;
  });

  const kept: SuggestedField[] = [];
  for (const candidate of all) {
    const clash = kept.find(
      (k) =>
        overlapRatio(k, candidate) >=
        (SIGNING_MARK.has(k.fieldType) && SIGNING_MARK.has(candidate.fieldType)
          ? SAME_SIGNING_MARK_OVERLAP
          : SAME_FIELD_OVERLAP),
    );
    if (!clash) {
      kept.push(candidate);
      continue;
    }
    // THE LOSER CAN STILL CONTRIBUTE ITS BINDING (2026-09-08).
    //
    // The two rungs answer different halves of the same question. The
    // ruled-line rung measures a blank exactly (the form drew it) but
    // only knows what it is FOR when the caption is in the dictionary.
    // Stew reads the caption in context and often knows exactly what it
    // is for, while placing the box by inference.
    //
    // Taking the better box and throwing away the only answer about
    // what fills it is the worst of both. So a kept, still-unbound text
    // field adopts the binding of a clashing proposal that has one —
    // geometry from the rung that measured it, meaning from the rung
    // that read it.
    //
    // Narrow on purpose: text fields only (a signature never merges a
    // value), and only onto a field that has NO binding already, so a
    // more-trusted rung's answer is never overwritten by a less-trusted
    // one.
    if (
      clash.fieldType === "text" &&
      !clash.mergeToken &&
      candidate.fieldType === "text" &&
      candidate.mergeToken
    ) {
      // Replaced rather than mutated: these objects belong to the
      // caller, and a merge that quietly edits its inputs is the kind of
      // shared-state bug that shows up two screens away.
      kept[kept.indexOf(clash)] = {
        ...clash,
        mergeToken: candidate.mergeToken,
        reason: candidate.reason,
      };
    }
  }

  // Reading order for display: page, then down, then across.
  return kept.sort(
    (a, b) => a.pageNumber - b.pageNumber || a.y - b.y || a.x - b.x,
  );
}

// ─── Keeping every proposal on its page ─────────────────────────────

/**
 * Fit suggestions inside their pages.
 *
 * WHY THIS EXISTS (staging round 3, 2026-08-23): the prep screen keeps a
 * field on the page everywhere a HUMAN touches one — placing, dragging,
 * resizing, nudging, repeat-on-every-page all run through clampToPage.
 * The suggestion path was the single route that skipped it, so five of
 * eight proposals on a real AAR purchase contract hung into the margin,
 * one by 155pt. The server then refused the send, which is correct but
 * meant the flagship document failed on the flagship path ("Accept all"
 * → "Send") every single time.
 *
 * So this is the one gate EVERY proposal passes through, whatever rung
 * produced it — the page's own form fields, a detected line, Stew, or a
 * saved layout recorded before this fix. Sizing the box to the line it
 * found (see suggestFromText) is what makes the result correct; this is
 * what makes it safe.
 *
 * Shrink before shove: a box wider than the space left keeps its left
 * edge and gives up width, because a signature line's START is the part
 * we actually detected — sliding it left would move it off the line it
 * belongs to. Only a box that cannot fit at all gets pushed.
 *
 * A page we have no size for is passed through untouched: inventing a
 * clamp from a guessed page size would move fields for no reason. The
 * server's own bounds check is the backstop there.
 */
export function fitSuggestionsToPages(
  suggestions: readonly SuggestedField[],
  pageSizes: readonly { pageNumber: number; width: number; height: number }[],
): SuggestedField[] {
  const byPage = new Map(pageSizes.map((p) => [p.pageNumber, p]));
  return suggestions.map((s) => {
    const page = byPage.get(s.pageNumber);
    if (!page) return s;
    // Shrink to the room actually left from this edge, not merely to
    // the page — that is what "keeps its left edge" means. A box that
    // would end up unusably narrow is shoved instead, because a 4pt
    // field nobody can tap is not a better answer than a moved one.
    const left = Math.max(0, Math.min(s.x, page.width - MIN_SUGGESTED_WIDTH));
    const top = Math.max(0, Math.min(s.y, page.height - MIN_SUGGESTED_HEIGHT));
    const width = Math.max(
      MIN_SUGGESTED_WIDTH,
      Math.min(s.width, page.width - left),
    );
    const height = Math.max(
      MIN_SUGGESTED_HEIGHT,
      Math.min(s.height, page.height - top),
    );
    // Only now can a shove be needed: a box already at its minimum that
    // still runs off has to come back inside.
    const x = Math.min(left, Math.max(0, page.width - width));
    const y = Math.min(top, Math.max(0, page.height - height));
    if (x === s.x && y === s.y && width === s.width && height === s.height) return s;
    return { ...s, x, y, width, height };
  });
}

// ─── Choosing what the model gets to read ───────────────────────────

/**
 * Which spans to send to the semantic pass, when a document has more
 * text than the budget allows.
 *
 * WHY THIS EXISTS (2026-09-01, Nate's listing contract): the budget used
 * to be spent by taking the FIRST 600 spans. Spans arrive in reading
 * order, so on the 8-page AAR Residential Listing Contract that is
 * roughly pages 1–3 — the model was never shown pages 4 through 8 at
 * all, and every signature block on the back half of the contract was
 * invisible to the one rung that could have found them. The cap looked
 * like a cost control and was actually silent truncation.
 *
 * Two rules fix it, and both matter:
 *
 *   1. EVERY PAGE GETS A SHARE. A per-page quota is filled first, so a
 *      long document can never spend its whole budget on its front
 *      matter. This is the part that makes page 8 reachable.
 *   2. CAPTIONS BEAT PROSE. Within a page, spans that look like they sit
 *      beside a blank — a run of underscores, a trailing colon, a short
 *      caption, a dollar or percent sign — are taken before dense legal
 *      paragraphs. An AAR contract is mostly boilerplate that will never
 *      carry a field, so ranking is what makes the quota go far enough.
 *
 * Reading order is restored before returning: the model is told to
 * anchor to span ids, and shuffling the page out of order would make its
 * job harder for no benefit.
 */
export function selectSpansForSuggester<T extends SpanInput>(
  spans: readonly T[],
  budget: number,
): T[] {
  if (budget <= 0) return [];
  if (spans.length <= budget) return [...spans];

  const order = new Map<T, number>();
  spans.forEach((s, i) => order.set(s, i));

  const byPage = new Map<number, T[]>();
  for (const s of spans) {
    const list = byPage.get(s.pageNumber);
    if (list) list.push(s);
    else byPage.set(s.pageNumber, [s]);
  }

  const pages = [...byPage.keys()].sort((a, b) => a - b);
  // At least one span per page even on a document with more pages than
  // budget — a page we send nothing from is a page we have decided,
  // silently, has no fields on it.
  const quota = Math.max(1, Math.floor(budget / pages.length));

  const picked = new Set<T>();
  const leftovers: T[] = [];
  for (const page of pages) {
    const ranked = [...(byPage.get(page) ?? [])].sort(
      (a, b) => fieldLikelihood(b.text) - fieldLikelihood(a.text) || (order.get(a) ?? 0) - (order.get(b) ?? 0),
    );
    for (const [i, span] of ranked.entries()) {
      if (i < quota) picked.add(span);
      else leftovers.push(span);
    }
  }

  // Whatever the quotas left unspent goes to the best of the rest,
  // wherever in the document they are.
  if (picked.size < budget) {
    leftovers.sort(
      (a, b) => fieldLikelihood(b.text) - fieldLikelihood(a.text) || (order.get(a) ?? 0) - (order.get(b) ?? 0),
    );
    for (const span of leftovers) {
      if (picked.size >= budget) break;
      picked.add(span);
    }
  }

  return spans.filter((s) => picked.has(s)).slice(0, budget);
}

/** How much a span looks like it sits next to somewhere a person writes.
 *  Deliberately crude and deterministic — it decides READING ORDER for a
 *  budget, never whether a field exists. */
function fieldLikelihood(text: string): number {
  const t = text.trim();
  if (!t) return 0;
  let score = 0;
  if (UNDERSCORE_RUN.test(t)) score += 5;
  if (/[:_]\s*$/.test(t)) score += 3;
  if (LABEL_RULES.some((r) => r.pattern.test(t))) score += 4;
  if (/[$%]/.test(t)) score += 2;
  if (/\b(date|name|address|initial|sign)\b/i.test(t)) score += 2;
  // Captions are short; a 200-character paragraph is the body of the
  // contract and is the first thing worth dropping.
  if (t.length <= 40) score += 2;
  else if (t.length > 150) score -= 2;
  return score;
}
